import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { ProductCard } from '../../../shared/product-card/product-card';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, of, switchMap } from 'rxjs';
import { SAVING_ARENA_API_CONFIG } from '../../../core/api/api.config';
import { FrontendProduct } from '../../../core/api/api.models';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../core/seo/seo.service';
import { SsrResponseService } from '../../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { StoreLogo } from '../../../shared/store-logo/store-logo';
import { AffiliateLinkService } from '../../../core/affiliate/affiliate-link.service';

/**
 * How many same-store products the "More from …" row shows.
 *
 * 12, because it is the first count that divides evenly by every column in the `.sav-product-grid`
 * ladder — 2 / 3 / 4 / 6 — so every screen shows all twelve on whole rows and none is trimmed away.
 * It was 6, which fits three of those four tiers; at four columns the row-completion trim cut it to
 * a single row of 4.
 */
const RELATED_LIMIT = 12;
/** Over-fetch by one so removing the product being viewed still leaves a full row. */
const RELATED_FETCH_LIMIT = RELATED_LIMIT + 1;

interface ProductInfoRow {
  label: string;
  value: string;
}

interface RelatedProduct {
  id: string;
  name: string;
  imageUrl: string;
  hoverImageUrl: string;
  price: string;
  originalPrice: string;
  path: string;
}

interface ProductPageData {
  categoryName: string;
  categoryPath: string;
  name: string;
  storeName: string;
  storePath: string;
  /** Merchant logo shown beside the store name; '' when the store has none. */
  storeLogoUrl: string;
  /** "-60%" for the image overlay — only set when the product carries a real markdown. */
  discountBadge: string;
  price: string;
  originalPrice: string;
  /** "SAVE EUR 8.99 (60%)" for the buy-box pill; '' when the feed gave us no markdown. */
  savingsBadge: string;
  /** '' when the feed does not say — we never guess availability. */
  stock: 'in' | 'out' | '';
  description: string;
  heroImageUrl: string;
  gallery: string[];
  affiliateLink: string;
  specRows: ProductInfoRow[];
}

@Component({
  selector: 'app-deals-product-page',
  imports: [Breadcrumb, RouterLink, ProductCard, StoreLogo],
  templateUrl: './deals-product-page.html',
  styleUrl: './deals-product-page.scss',
})
export class DealsProductPage {
  private readonly route = inject(ActivatedRoute);
  private readonly affiliate = inject(AffiliateLinkService);
  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);

  protected readonly product = signal<FrontendProduct | null>(null);

  /** The visible trail used to start at "Deals" — no Home crumb at all, unlike every other page. */
  protected readonly crumbs = computed(() => {
    const page = this.page();
    if (!page) return breadcrumbTrail({ label: 'Deals', path: '/collection' });
    return breadcrumbTrail(
      { label: 'Deals', path: '/collection' },
      ...(page.categoryName ? [{ label: page.categoryName, path: page.categoryPath }] : []),
      { label: page.name },
    );
  });
  protected readonly related = signal<RelatedProduct[]>([]);
  protected readonly isLoading = signal(true);
  /** Distinguishes "still fetching" from "this product does not exist", so the 404 copy is honest. */
  protected readonly notFound = computed(() => !this.isLoading() && this.product() === null);

  /** Gallery image in the main frame; the thumb strip swaps it on hover/click. */
  protected readonly activeImage = signal('');

  // Main-image magnifier (hover to zoom, follows the cursor) — same behaviour as the Shop gallery.
  protected readonly zoomActive = signal(false);
  private readonly zoomX = signal(50);
  private readonly zoomY = signal(50);
  protected readonly zoomOrigin = computed(() => `${this.zoomX()}% ${this.zoomY()}%`);

  protected setImage(image: string): void {
    this.activeImage.set(image);
  }

  protected onZoomMove(event: MouseEvent): void {
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    this.zoomX.set(((event.clientX - rect.left) / rect.width) * 100);
    this.zoomY.set(((event.clientY - rect.top) / rect.height) * 100);
  }

  /** Guards against a slower earlier request overwriting a newer product's related row. */
  private relatedRequestId = 0;

  protected readonly page = computed<ProductPageData | null>(() => {
    const product = this.product();
    if (!product) return null;

    const store = product.store;
    const categoryName = (product.category?.name ?? '').trim() || 'Deals';
    const price = (product.newPrice ?? '').trim();
    const originalPrice = (product.oldPrice ?? '').trim();
    const discountPercent = Number(product.discountPercent ?? 0);

    return {
      categoryName,
      // The API's `categoryCollectionSlug`, not a slug built from the name here. Slugifying the name
      // dropped ampersands — "Health & Beauty" became `health-beauty` where the category's real slug
      // is `health-and-beauty` — so 38 categories were linked to URLs that did not exist, one of
      // them from 153 pages. The API also leaves this empty when the category has too few products
      // to have a page at all, and the template then renders the name without a link.
      categoryPath: (product['categoryCollectionSlug'] as string | undefined)?.trim()
        ? `/collection/${product['categoryCollectionSlug']}`
        : '',
      name: (product.title || product.name || '').trim(),
      storeName: (store?.name ?? '').trim(),
      // "Visit <store>" from a product page belongs on that store's PRODUCTS page, not its coupons
      // page — the visitor is shopping, not hunting for a code. The API returns `dealsStoreKey` only
      // when that page exists (the store has to clear the product threshold), so the coupons page
      // stays as the fallback rather than linking somewhere that 404s.
      storePath: this.buildStorePath(product, store?.slug ?? ''),
      // Networks hand us absolute logo URLs; locally-uploaded ones are /uploads paths. assetUrl
      // handles both, and a logo that fails to load is dropped rather than shown broken.
      storeLogoUrl: this.assetUrl(store?.logoImagePath ?? ''),
      discountBadge: discountPercent > 0 ? `-${discountPercent}%` : '',
      price,
      originalPrice,
      savingsBadge: this.buildSavingsBadge(product, discountPercent),
      stock: product.inStock === true ? 'in' : product.inStock === false ? 'out' : '',
      description: (product.description ?? '').trim(),
      heroImageUrl: this.assetUrl(product.thumbnailImagePath ?? ''),
      gallery: this.buildGallery(product),
      // The `/go/p/:id` redirect, like the product CARDS already use — not the merchant URL.
      // This was the raw affiliate link straight in the href, so it was readable in the page source
      // and the click went unrecorded, while every other buy button on the site went through /go/.
      //
      // It also settles what to do when a product has no link of its own: the redirect resolves the
      // store's, server-side. That fallback used to live here and read `store.affiliateLink`, which
      // the API no longer sends — deliberately, since sending it is what leaked the raw URL.
      affiliateLink: this.buildGoUrl(product),
      specRows: this.buildSpecRows(product),
    };
  });

  constructor() {
    this.route.paramMap
      .pipe(
        map((params) => (params.get('productSlug') ?? '').trim()),
        switchMap((key) => {
          this.isLoading.set(true);
          this.product.set(null);
          this.related.set([]);
          this.activeImage.set('');
          this.relatedRequestId += 1;

          if (!key) return of(null);

          // The API resolves an id or a slug — imported products only have an id.
          return this.api.getProduct(key).pipe(
            map((response) => (response.success ? response.data : null)),
            catchError((error: unknown) => {
              this.ssrResponse.noteFetchFailure(error);
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((product) => {
        this.product.set(product);
        this.activeImage.set(product ? (this.buildGallery(product)[0] ?? '') : '');
        this.isLoading.set(false);
        this.applyProductSeo();
        if (product) this.loadRelated(product);
      });
  }

  private loadRelated(product: FrontendProduct): void {
    const storeId = product.store?.id;
    if (!storeId) return;

    const requestId = this.relatedRequestId;

    this.api
      .getProducts({ store: storeId, page: 1, limit: RELATED_FETCH_LIMIT })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (requestId !== this.relatedRequestId || !response?.success) return;

        this.related.set(
          (response.data ?? [])
            .filter((candidate) => candidate.id !== product.id)
            .slice(0, RELATED_LIMIT)
            .map((candidate) => this.toRelated(candidate)),
        );
      });
  }

  private toRelated(product: FrontendProduct): RelatedProduct {
    return {
      id: product.id,
      name: (product.title || product.name || '').trim(),
      imageUrl: this.assetUrl(product.thumbnailImagePath ?? ''),
      hoverImageUrl: this.toHoverImageUrl(product),
      price: (product.newPrice ?? '').trim(),
      originalPrice: (product.oldPrice ?? '').trim(),
      path: `/${this.routeKey(product)}`,
    };
  }

  // First gallery angle that genuinely differs from the thumbnail; '' when the feed gave only one.
  private toHoverImageUrl(product: FrontendProduct): string {
    const mainImagePath = product.thumbnailImagePath ?? '';
    const alternate = (product.galleryImagePaths ?? []).find(
      (path) => path && path !== mainImagePath,
    );

    return alternate ? this.assetUrl(alternate) : '';
  }

  /** Thumbnail first, then the feed's alternate angles; blanks and repeats dropped. */
  private buildGallery(product: FrontendProduct): string[] {
    const paths = [product.thumbnailImagePath ?? '', ...(product.galleryImagePaths ?? [])];

    return paths
      .map((path) => this.assetUrl((path ?? '').trim()))
      .filter((url, index, all) => url.length > 0 && all.indexOf(url) === index);
  }

  /**
   * "SAVE EUR 8.99 (60%)" from the numeric `savingsAmount` the sync computed, using whatever currency
   * the price string already carries — the prices are pre-formatted text, not numbers. Falls back to
   * the bare percentage when the feed priced the markdown but not the amount.
   */
  private buildSavingsBadge(product: FrontendProduct, discountPercent: number): string {
    const amount = Number(product.savingsAmount ?? 0);
    if (!(amount > 0)) return discountPercent > 0 ? `SAVE ${discountPercent}%` : '';

    const currency = (product.currency ?? '').trim() || this.currencyOf(product.newPrice ?? '');
    const separator = /[A-Za-z]$/.test(currency) ? ' ' : '';
    const saved = `SAVE ${currency}${separator}${amount.toFixed(2)}`;

    return discountPercent > 0 ? `${saved} (${discountPercent}%)` : saved;
  }

  /**
   * Rows for the specifications table — only what the feed actually supplied, no invented specs.
   * Store, category and availability are deliberately absent: they render in the buy box instead.
   */
  private buildSpecRows(product: FrontendProduct): ProductInfoRow[] {
    const rows: ProductInfoRow[] = [];
    const brand = (product.brand ?? '').trim();

    if (brand) rows.push({ label: 'Brand', value: brand });
    (product.options ?? []).forEach((option) => {
      const label = (option?.name ?? '').trim();
      const value = (option?.value ?? '').trim();
      if (label && value) rows.push({ label, value });
    });

    return rows;
  }

  /** Prefer the SEO slug; imported products that predate slug allocation still resolve by id. */
  private routeKey(product: FrontendProduct): string {
    return (product.slug ?? '').trim() || product.id;
  }

  private currencyOf(value: string): string {
    const match = value.trim().match(/^[^\d]+/);
    return match ? match[0].trim() : '';
  }

  /**
   * The store's products page when it has one, its coupons page otherwise.
   *
   * `/collection/<category>/<storeKey>` is the canonical form; `/deals/<storeKey>` is the same page
   * when the API could not tell us which collection the store sits in.
   */
  private buildStorePath(product: FrontendProduct, couponSlug: string): string {
    const key = String(product['dealsStoreKey'] ?? '').trim();
    if (!key) return couponSlug ? `/${couponSlug}` : '';

    // `/collection/<category>/<store>` is the only address this page has now — `/deals/<store>` was
    // retired. Without a collection there is nowhere to point, so the template shows no link rather
    // than one to a URL that answers 410.
    const collection = String(product['dealsStoreCategorySlug'] ?? '').trim();
    return collection ? `/collection/${collection}/${encodeURIComponent(key)}` : '';
  }

  /**
   * The product's click-out, .
   *
   * Empty when the product has neither an id nor a link, so the template renders no button at all
   * rather than one that goes nowhere.
   */
  private buildGoUrl(product: FrontendProduct): string {
    const id = String(product.id ?? '').trim();
    if (!id) return '';

    const hasDestination = Boolean((product.affiliateLink ?? '').trim() || product.store);
    if (!hasDestination) return '';

    return this.affiliate.productUrl(id);
  }

  private assetUrl(path: string): string {
    const trimmed = path.trim();
    if (!trimmed || /^https?:\/\//i.test(trimmed)) return trimmed;
    const base = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    return `${base}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
  }

  /**
   * A key with no product behind it is a real 404, not a 200 fallback product (spec §7).
   *
   * No Product/Offer schema is emitted: we have no ratings or review counts for these products, and
   * structured data must never assert values we cannot back.
   */
  private applyProductSeo(): void {
    const page = this.page();

    if (!page) {
      // Only claim "not found" once the request has actually resolved.
      if (this.isLoading()) return;

      this.ssrResponse.setNotFound();
      this.seo.apply({
        title: 'Product not found | SavingArena',
        description: 'The product you are looking for is not available on SavingArena.',
        noindex: true,
      });
      this.seo.setJsonLd([]);
      return;
    }

    // Canonical always points at the slug URL, never the id one. Both resolve, so without this the
    // same product would self-canonicalise under two addresses and split its ranking.
    const product = this.product();
    const path = `/${product ? this.routeKey(product) : ''}`;
    const url = this.seo.absolute(path);
    const title = page.storeName
      ? `${page.name}: ${page.storeName} Deal | SavingArena`
      : `${page.name} | SavingArena`;
    const description = page.description || `${page.name} available at ${page.storeName}.`;

    this.seo.apply({
      title,
      description,
      canonicalPath: path,
      image: page.heroImageUrl || undefined,
    });

    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
    ]);
  }
}
